import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { useHistory, Link as RouterLink } from 'react-router-dom'
import { ROUTE_PAGE_RESET_PASSWORD, ROUTE_PAGE_PRIVACY_POLICY, ROUTE_PAGE_TERMS_CONDITIONS } from "../../routers/routes";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { LoginForm } from "../../components/loginForm/loginForm";
import * as yup from "yup";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { handleAxiosErrors } from "../../helpers/errorHandling";
import { Grid, Link, Typography } from '@mui/material';
import { Capacitor } from "@capacitor/core";
import { Preferences } from '@capacitor/preferences';
import {
	BiometricAuth,
	CheckBiometryResult,
	BiometryError,
	BiometryErrorType,
	AndroidBiometryStrength
} from '@aparajita/capacitor-biometric-auth';
import { showPopup } from '../../components/globalPopup/globalPopupService';
import AuthBackground from "../../theme/mantis/assets/images/auth/authBackground";
import AuthCard from "../../theme/mantis/sections/auth/authCardLogin";
import loginimg from '../../../src/assets/images/login-img.jpg';



const Login = () => {
	const history = useHistory();
	const { t } = useTranslation(PAGE_LOGIN)
	const { userLogin, checkAPIConnectivity } = useUserAction();
	const { loading, errorLogin, errorConnectivity, alterPasswordOnLogin, isLoggedIn } = useUserData();
	const [isLoginOneTime, setIsLoginOneTime] = useState('')

	useEffect(() => {
		document.title = t('IMS Financial Consulting | Login')
	
		const searchParams = new URLSearchParams(window.location.search);
		let cond = searchParams.get('cond');
		if(Capacitor.isNativePlatform()) {
			cond = !cond ? sessionStorage.getItem('cond') : cond
		}

		if (cond) {
			// Map cond values to messages
			const condMessages = {
				'1': t('Your account has been activated. You can now log in'),
				'2': t('The activation link is not valid'),
				'3': t('The activation link is not valid or has already been used'),
			};


			const message = condMessages[cond];

			if (message) {
				showPopup({
					title: '',
					message,
					onConfirm: () => {},
				});
				sessionStorage.removeItem('cond');
			}
		}
	}, [t]);


	const onSubmit = async (values) => {
		await Preferences.set({ key: 'username', value: values.username })
		await Preferences.set({ key: 'password', value: values.password })
		userLogin(values)
	}

	const validationSchema = yup.object({
		username: yup
			.string()
			.required(t('required')),
		password: yup
			.string()
			.required(t('required')),
	})

	const formik = useFormik({
		initialValues: {
			username: '',
			password: '',
			rememberMe: false,
			email: '',
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})

	useEffect(() => {
		const fetchStoredCredentials = async () => {
			const username = await Preferences.get({ key: 'username' });
			//const password = await Preferences.get({key: 'password'});
			setIsLoginOneTime(username.value)

			if (username.value) {
				formik.setFieldValue('username', username.value)
			}
			/*if (password.value) {
				formik.setFieldValue('password', password.value)
			}*/
		};

		fetchStoredCredentials()
		checkAPIConnectivity()
	}, [checkAPIConnectivity])

	useEffect(() => {
		if (errorLogin) {

			showPopup({
				title: '',
				message:  t(errorLogin),
				onConfirm: () => {
	
				}
			  });
			//   enqueueSnackbar(handleAxiosErrors(errorLogin, t(errorLogin)), {variant: 'error'})
		}
	}, [errorLogin, t])

	useEffect(() => {
		if (errorConnectivity) {
			enqueueSnackbar(handleAxiosErrors(errorConnectivity, t(errorConnectivity)), {
				variant: 'error'
			}
			)
		}
	}, [errorConnectivity, t])

	useEffect(() => {
		if (isLoggedIn && alterPasswordOnLogin) {
			history.push(getRouteUrl(ROUTE_PAGE_RESET_PASSWORD, {}, { username: formik.values.username }))
		}
	}, [alterPasswordOnLogin, isLoggedIn])

	const handleBiometricLogin = async () => {
		try {
			const biometryInfo: CheckBiometryResult = await BiometricAuth.checkBiometry()
			

			if (biometryInfo.isAvailable) {
				
				await BiometricAuth.authenticate({
					reason: 'Please authenticate',
					cancelTitle: 'Cancel',
					allowDeviceCredential: true,
					iosFallbackTitle: 'Use passcode',
					androidTitle: 'Biometric login',
					androidSubtitle: 'Log in using biometric authentication',
					androidConfirmationRequired: false,
					androidBiometryStrength: AndroidBiometryStrength.weak
				})

				const username = await Preferences.get({ key: 'username' })
				const password = await Preferences.get({ key: 'password' })

				if (username.value && password.value) {
					formik.setFieldValue('username', username.value)
					formik.setFieldValue('password', password.value)
					userLogin({ username: username.value, password: password.value })
				} else {
					enqueueSnackbar('Biometric authentication failed', { variant: 'error' });
				}
			} else {
				enqueueSnackbar('Biometry not available', { variant: 'error' })
			}
		} catch (error) {
			if (error instanceof BiometryError && error.code !== BiometryErrorType.userCancel) {
				enqueueSnackbar('Biometric authentication error: ' + error.message, { variant: 'error' })
			}
		}
	}

	return (
		<Grid container className='mt-5'>
			<AuthBackground />
			<Grid container spacing={2}>
				<Grid item xs={12} sm={7} md={7}>
					<Grid sx={{
						display: {
							xs: 'none',
							sm: 'block',
						},
						backgroundImage: `url(${loginimg})`, backgroundRepeat: 'no-repeat',
						backgroundSize: 'cover',
						width: '100%',
						backgroundPosition: 'right',
						height: '100vh',
						minHeight: '100%',
						height: {
							xs: '272px',
							sm: '100vh',
							md: '100vh',
						},
						boxShadow: {xs: 'inset 0px -25px 22px #ffffff', sm:'unset', md:'unset'}
					}}>
					</Grid>
				</Grid>
				<Grid item xs={12} sm={5} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
					<Grid  sx={{ alignSelf: 'center', marginTop: 'auto', padding: '32px 24px 0 24px' }}>
						<AuthCard>
							<LoginForm
								sx={{marginBottom: '20px'}}
								formik={formik}
								loading={loading}
								handleBiometricLogin={handleBiometricLogin}
								isLoginOneTime={isLoginOneTime}
							/>
						</AuthCard>
					</Grid>
					<Grid sx={{ display: 'flex', alignSelf: 'flex-end', marginTop: 'auto', zIndex: '10', width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', padding: '0 24px 32px 24px' }} >
						<Typography 
							variant="h5"
							sx={{
								width: { xs: '100%', sm: '100%', md: 'initial' }, 
								textAlign: { xs: 'center', sm: 'center', md: 'left' },
								marginTop:'30px',
							}}
						>
							<Link
								component={RouterLink}
								className='color-default p-sm'
								to={getRouteUrl(ROUTE_PAGE_PRIVACY_POLICY)}
								target={!Capacitor.isNativePlatform() && "_blank"}
							>
								{t('Privacy policy')}
							</Link>
						</Typography>
						<Typography
							variant="h5" 
							sx={{ 
								width: { xs: '100%', sm: '100%', md: 'initial' }, 
								textAlign: { xs: 'center', sm: 'center', md: 'right' },
								marginTop:'30px',
							}}
						>
							<Link
								component={RouterLink}
								className='color-default p-sm'
								to={getRouteUrl(ROUTE_PAGE_TERMS_CONDITIONS)}
								target={!Capacitor.isNativePlatform() && "_blank"}
							>
								{t('Terms & Conditions')}
							</Link>
						</Typography>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default Login